import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  List,
  message,
  Button,
  Layout,
  Space,
  Modal,
  Input,
  Form,
} from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import TranslateBtn from "../../components/product/TranslateBtn";
import { ApiManager, Locale } from "../../utils";
import { getUserInfo } from "../../utils/redux/reducers/User";
import Base from "../Base";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();
const base = Base.getInstance();

function ArticleTagList() {
  const fetching = useRef(false);
  const fetchData = useRef(() => {});
  const user = useSelector((state) => getUserInfo(state));
  const [loading, setLoading] = useState(false);
  const [data, setListData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  fetchData.current = async () => {
    if (!user.token) return;
    if (fetching.current) return;
    fetching.current = true;
    setLoading(true);
    const apiUrl = api.article.tagList;
    try {
      const res = await apiUrl.run({ token: user.token });
      if (res.result) {
        setListData(res.data || []);
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      base.handleApiError(apiUrl, error);
    } finally {
      fetching.current = false;
      setLoading(false);
    }
  };

  function onDeleteItem(id) {
    base.showModal(
      i18n.t("sales.deleteWarning"),
      Base.Modal.confirm,
      async () => {
        const apiUrl = api.article.tagDelete;
        try {
          const res = await apiUrl.run({
            token: user.token,
            path: { id },
          });
          if (res.result) {
            fetchData.current();
          } else {
            base.showMessage(res.message, Base.Message.warning);
          }
        } catch (error) {
          base.handleApiError(apiUrl, error);
        }
      }
    );
  }

  function onFinish() {
    form
      .validateFields()
      .then(async (values) => {
        if (!user.token) return;
        let apiUrl = api.article.tagAdd;
        const apiConfig = {
          token: user.token,
          data: { ...values },
        };
        if (editing && editing.id) {
          apiUrl = api.article.tagUpdate;
          apiConfig.path = { id: editing.id };
        }
        try {
          setLoading(true);
          const res = await apiUrl.run(apiConfig);
          if (res.result) {
            setShowModal(false);
            setEditing(null);
            form.resetFields();
            fetchData.current();
          } else {
            base.showModal(res.message, Base.Modal.warning);
          }
        } catch (error) {
          base.handleApiError(apiUrl, error);
        } finally {
          setLoading(false);
        }
      })
      .catch(() => {});
  }

  useEffect(() => {
    fetchData.current();
  }, []);

  return (
    <Layout>
      <Layout.Content>
        <List
          header={
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditing(null);
                  form.resetFields();
                  setShowModal(true);
                }}
              >
                {i18n.t("article.tagAdd")}
              </Button>
            </Space>
          }
          bordered
          size="large"
          style={{ backgroundColor: "#fff" }}
          loading={loading}
          dataSource={data}
          rowKey={(record) => `tag-${record.id}`}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button
                  key="edit"
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditing(item);
                    form.setFieldsValue({
                      zh_name: item.zh_name,
                      ug_name: item.ug_name,
                    });
                    setShowModal(true);
                  }}
                >
                  {i18n.t("product.productItem.actionEdit")}
                </Button>,
                <Button
                  key="delete"
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onDeleteItem(item.id)}
                >
                  {i18n.t("sales.activity.delete")}
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={i18n.getPropNameByLocale(item, "name")}
                description={`ID: ${item.id} · ${item.zh_name} / ${item.ug_name}`}
              />
            </List.Item>
          )}
        />
      </Layout.Content>
      <Modal
        title={
          editing
            ? i18n.t("article.tagEdit")
            : i18n.t("article.tagAdd")
        }
        open={showModal}
        onCancel={() => {
          setShowModal(false);
          setEditing(null);
          form.resetFields();
        }}
        onOk={() => onFinish()}
        okText={i18n.t("base.modal.okText")}
        cancelText={i18n.t("base.modal.cancelText")}
        okButtonProps={{ loading, disabled: loading }}
      >
        <Form form={form} layout="vertical">
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item
                name="zh_name"
                label={i18n.t("sales.mini.zhTitle")}
                rules={[
                  { required: true, message: i18n.t("sales.zhTitleWarning") },
                ]}
              >
                <Input
                  addonAfter={
                    <TranslateBtn
                      context={getFieldValue("zh_name")}
                      onFinish={(value) =>
                        setFieldsValue({
                          ug_name: value,
                        })
                      }
                      from="zh"
                      to="ug"
                    />
                  }
                />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item
                name="ug_name"
                label={i18n.t("sales.mini.ugTitle")}
                rules={[
                  { required: true, message: i18n.t("sales.ugTitleWarning") },
                ]}
              >
                <Input
                  addonAfter={
                    <TranslateBtn
                      context={getFieldValue("ug_name")}
                      onFinish={(value) =>
                        setFieldsValue({
                          zh_name: value,
                        })
                      }
                      from="ug"
                      to="zh"
                    />
                  }
                />
              </Form.Item>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}

export default ArticleTagList;
